import { AuthMethodProviderMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/auth-method-provider.enum';
import type { DeviceIdScope } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import { DeviceIdScopeMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import type { ResponseUserGrantDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/dtos/response.user-grant.dto';
import type { NamedRole } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';

export type AuthStateTokenAndOwnGrant = {
  authStateTokens: AuthStateTokens;
  ownGrant: ResponseUserGrantDto | undefined;
};

export type AuthResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  message: string;
};

export enum FeaturesAmlDeskWithGrants {
  KnowYourCustomer = 'KnowYourCustomer',
  RiskAnalysis = 'RiskAnalysis',
  LocationAnalysis = 'LocationAnalysis',
  Academy = 'Academy',
}

export const FeatureRoleMap: Record<FeaturesAmlDeskWithGrants, NamedRole[]> = {
  [FeaturesAmlDeskWithGrants.KnowYourCustomer]: [NamedRoleMap.WorkItemUser, NamedRoleMap.KycRiskEvaluationApiUser],
  [FeaturesAmlDeskWithGrants.RiskAnalysis]: [NamedRoleMap.RiskAnalysisCustomer],
  [FeaturesAmlDeskWithGrants.LocationAnalysis]: [NamedRoleMap.LocationAnalysisUser],
  [FeaturesAmlDeskWithGrants.Academy]: [NamedRoleMap.AcademyUser, NamedRoleMap.AcademyCoordinator],
};

export type AuthStateTokens = {
  accessTokenKerberos: string | null;
  refreshTokenKerberos: string | null;
  /**
   * Raw Hanko session JWT, used only for the initial token exchange against the
   * ADP `authentication/sign-in` endpoint, and null at every other point in the
   * session — the ADP refresh token drives all subsequent renewals.
   */
  accessTokenHanko: string | null;
};

export const BearerTokenKindMap = {
  Hanko: 'hanko',
  Platform: 'platform',
} as const;

export type BearerTokenKind = (typeof BearerTokenKindMap)[keyof typeof BearerTokenKindMap];

/**
 * Mirrors the Luna `auth_method_provider` enum, whose integer value the ADP
 * platform token carries in its `asrc` (auth source) claim. `Azure` is retained
 * because the platform still issues it for AML admin desk sessions, even though
 * the AML desk itself no longer authenticates through Azure.
 */
export const AuthSourceMap = {
  Azure: AuthMethodProviderMap.MsAzureADB2C,
  Hanko: AuthMethodProviderMap.Hanko,
} as const;

export type AuthSource = (typeof AuthSourceMap)[keyof typeof AuthSourceMap];

export const AuthSourceClaim = 'asrc';

/**
 * Display names for every `asrc` value the platform stamps, incl. the admin desk providers an impersonated
 * or admin token may carry. Diagnostics only; the AML desk's own resolution stays on {@link AuthSourceMap}.
 */
export const AuthSourceLabelMap: Readonly<Partial<Record<number, string>>> = {
  [AuthMethodProviderMap.MsAzureADB2C]: 'Azure B2C',
  [AuthMethodProviderMap.MSAzureAD]: 'Entra ID',
  [AuthMethodProviderMap.Hanko]: 'Hanko',
};

export const DeviceIdScopeClaim = 'dids';

export const DeviceIdScopeLabelMap: Readonly<Record<DeviceIdScope, string>> = {
  [DeviceIdScopeMap.AmlDesk]: 'AML desk',
  [DeviceIdScopeMap.AmlAdminDesk]: 'AML admin desk',
  [DeviceIdScopeMap.ClientCredentials]: 'Client credentials',
  [DeviceIdScopeMap.OnOffice]: 'onOffice',
};

/**
 * Backend error codes that indicate the user is not active (disabled, blocked,
 * or the auth method is expired) — on both the sign-in and the refresh call.
 * `-519` is the canonical code defined in `compliance-shared-ts`.
 */
export const UserNotActiveErrorCodes = [-519] as const;

export enum LogoutReason {
  Manual = 'manual',
  Inactivity = 'inactivity',
  SessionExpired = 'sessionExpired',
}

export const GrantLoadingStateMap = {
  Idle: 'idle',
  Loading: 'loading',
  Success: 'success',
  Error: 'error',
  Empty: 'empty',
} as const;

export type GrantLoadingState = (typeof GrantLoadingStateMap)[keyof typeof GrantLoadingStateMap];
